import axios from 'axios';

/**
 * fetchList — B-24 (26 Aug 2026): read the truncation metadata the API now
 * sends alongside capped list endpoints.
 *
 * The backend reports the pre-LIMIT total in `X-Total-Count` and whether rows
 * were dropped in `X-Has-More` (see backend/src/lib/listMeta.js). Headers were
 * chosen over a `{ items, total }` envelope because the shipped TestFlight and
 * Play builds bundle an older web build and parse these responses as bare
 * arrays — an envelope would have broken every tester's app until the Sept
 * store build.
 *
 * Returns { items, total, hasMore }. If the headers are absent — an older
 * endpoint, or a cross-origin response where they weren't exposed — it
 * degrades to the old behaviour (total = items.length, hasMore = false)
 * rather than throwing.
 */
export async function fetchList(url, config) {
  const res = await axios.get(url, config);
  const items = Array.isArray(res.data) ? res.data : [];
  const rawTotal = Number(res.headers?.['x-total-count']);
  return {
    items,
    total: Number.isFinite(rawTotal) ? rawTotal : items.length,
    hasMore: res.headers?.['x-has-more'] === 'true',
  };
}

/**
 * Same, for endpoints that return an object rather than an array (the QR
 * wallet, the attribution report). Gives you the body plus the meta.
 */
export async function fetchWithMeta(url, config) {
  const res = await axios.get(url, config);
  const rawTotal = Number(res.headers?.['x-total-count']);
  return {
    data: res.data,
    total: Number.isFinite(rawTotal) ? rawTotal : null,
    hasMore: res.headers?.['x-has-more'] === 'true',
  };
}

/**
 * "20 of 34" when truncated, "34" when not — for count badges that used to
 * render `rows.length` and quietly asserted a wrong number once the cap bit.
 */
export function formatCount(shown, total) {
  if (!Number.isFinite(total) || total <= shown) return String(shown);
  return `${shown} of ${total}`;
}
